import { useState, useEffect } from 'react';
import { FileStack, ShieldCheck, Share2, Clock, IndianRupee, Building2, FileText, AlertTriangle } from 'lucide-react';
import { T } from '@design/index';
import { GOV_TYPE, GOV_COMPLIANCE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi } from '../api';
import type { InsurancePolicy, InsuranceClaim, PatientCertificateDoc } from '../types';
import NHCIdBadge from '../components/NHCIdBadge';
import NeoVerifyBadge from '../components/NeoVerifyBadge';
import InstitutionShareModal from '../components/InstitutionShareModal';
import Spinner from '../../../components/ui/Spinner';

function fmtDate(d?: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtCurrency(n?: number | null) {
  if (!n) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

const STATUS_COLORS: Record<string, string> = {
  APPROVED: 'bg-emerald-50 text-emerald-700',
  PENDING: 'bg-amber-50 text-amber-700',
  SUBMITTED: 'bg-blue-50 text-blue-700',
  REJECTED: 'bg-red-50 text-red-700',
  SETTLED: 'bg-teal-50 text-teal-700',
  UNDER_REVIEW: 'bg-purple-50 text-purple-700',
};

export default function ClaimsDocPack() {
  const [policies, setPolicies] = useState<InsurancePolicy[]>([]);
  const [claims, setClaims] = useState<InsuranceClaim[]>([]);
  const [claimCerts, setClaimCerts] = useState<PatientCertificateDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [nhcId, setNhcId] = useState('');
  const [shareTarget, setShareTarget] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      patientApi.getInsurancePolicies(),
      patientApi.getInsuranceClaims(),
      patientApi.getCertificates({ type: 'INSURANCE_CLAIM_CERTIFICATE' }),
      patientApi.getProfile(),
    ]).then(([p, c, cc, prof]) => {
      if (alive) { setPolicies(p); setClaims(c); setClaimCerts(cc); setNhcId(prof.nhcId); }
    }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6" style={govPageContainer()}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="flex items-center gap-2" style={GOV_TYPE.pageTitle}>
            <FileStack className="w-6 h-6 text-cyan-600" /> Claims Document Pack
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Insurance claims, documents & NeoVerify-linked proofs</p>
        </div>
        {nhcId && <NHCIdBadge nhcId={nhcId} />}
      </div>

      {/* Consent note */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2 text-xs text-amber-800">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <p>Claim documents can be shared with your insurer or TPA. NeoVerify proves document authenticity — sharing does not expose your full medical history.</p>
      </div>

      {/* Policies */}
      <div>
        <h2 className="text-sm font-bold text-gray-900 mb-3">Linked Policies</h2>
        {policies.length === 0 ? (
          <div className="card p-6 text-center text-gray-400 text-sm">No policies found</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {policies.map(p => (
              <div key={p.id} className="card p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-4 h-4 text-cyan-600" />
                  <p className="text-sm font-semibold text-gray-900">{p.providerName}</p>
                </div>
                <div className="space-y-1 text-xs text-gray-600">
                  <p>Policy: <span className="font-mono">{p.policyNumber}</span></p>
                  {p.planName && <p>Plan: {p.planName}</p>}
                  <p>Coverage: {fmtCurrency(p.coverageAmount)}</p>
                  <p>Valid: {fmtDate(p.validFrom)} — {fmtDate(p.validTo)}</p>
                </div>
                <span className={`inline-block mt-2 text-[10px] font-semibold px-2 py-0.5 rounded ${STATUS_COLORS[p.status] || 'bg-gray-50 text-gray-500'}`}>{p.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Claims */}
      <div>
        <h2 className="text-sm font-bold text-gray-900 mb-3">Claims</h2>
        {claims.length === 0 ? (
          <div className="card p-6 text-center text-gray-400 text-sm">No claims found</div>
        ) : (
          <div className="space-y-3">
            {claims.map(c => (
              <div key={c.id} className="card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    {/* A pre-authorisation is not a claim, and its reference
                        is numbered PA-… — calling it "Claim #" misnames what
                        the insurer is being asked for. */}
                    <p className="text-sm font-semibold text-gray-900">
                      {c.type === 'Preauth' ? 'Pre-auth' : 'Claim'} #{c.claimNumber}
                    </p>
                    {c.description && <p className="text-xs text-gray-500 mt-0.5">{c.description}</p>}
                    <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-gray-500">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(c.claimDate)}</span>
                      {c.hospitalName && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {c.hospitalName}</span>}
                    </div>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded shrink-0 ${STATUS_COLORS[c.status] || 'bg-gray-50 text-gray-500'}`}>{c.status}</span>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-3 pt-2 border-t border-gray-100">
                  <div>
                    <p className="text-[10px] text-gray-400">Claimed</p>
                    <p className="text-sm font-bold text-gray-900">{fmtCurrency(c.amount)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400">Settled</p>
                    <p className="text-sm font-bold text-emerald-600">{fmtCurrency(c.settledAmount)}</p>
                  </div>
                  <div>
                    {/* Until the insurer settles, the split is UNKNOWN — never
                        present the full claim as the patient's liability. */}
                    <p className="text-[10px] text-gray-400">Patient Payable</p>
                    <p className="text-sm font-bold text-amber-600">
                      {c.settledAmount != null ? fmtCurrency(c.amount - (c.settledAmount || 0)) : 'Decided by insurer'}
                    </p>
                  </div>
                </div>

                <div className="mt-3 pt-2 border-t border-gray-100">
                  <p className="text-[10px] font-semibold text-gray-500 mb-2">Documents</p>
                  <p className="text-[10px] text-gray-400 flex items-center gap-1">
                    <FileText className="w-3 h-3" /> Attach supporting documents from your Health Vault when sharing with the insurer — automatic attachment is not available yet.
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* NeoVerify Claim Certificates */}
      {claimCerts.length > 0 && (
        <div>
          <h2 className="text-sm font-bold text-gray-900 mb-3">NeoVerify Claim Certificates</h2>
          <div className="space-y-2">
            {claimCerts.map(c => (
              <div key={c.id} className="card p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{c.title}</p>
                  <p className="text-[10px] text-gray-500">{c.issuerName} — {fmtDate(c.issueDate)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <NeoVerifyBadge status={c.verificationStatus} neoVerifyId={c.neoVerifyId} />
                  <button onClick={() => setShareTarget({ id: c.id, title: c.title })}
                    className="text-[11px] text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                    <Share2 className="w-3 h-3" /> Share
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p style={GOV_COMPLIANCE.text}>Claim records come from your NeoHealthCard account · Approval and settlement are decided by your insurer</p>

      {shareTarget && (
        <InstitutionShareModal
          open={!!shareTarget}
          onClose={() => setShareTarget(null)}
          certificateId={shareTarget.id}
          certificateTitle={shareTarget.title}
        />
      )}
    </div>
  );
}
